'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import { IReview } from '@/entities/review/types';
import { QUERY_KEYS } from '@/shared/const/query-keys';
import { useSession } from '@/shared/hooks/use-session';
import { useToast } from '@/components/ui/use-toast';
import { getApiUrl } from '@/shared/utils/getApiUrl';
import { useRouter } from '@/shared/navigation';

type CreateReviewMutationOptions = {
  /** после успеха: переход на МК или назад (закрыть intercept + bottom sheet) */
  afterSuccess?: 'push' | 'back';
};

export const useCreateReviewMutation = (
  masterclassId: number,
  masterclassWordId: string,
  options?: CreateReviewMutationOptions,
) => {
  const { token } = useSession();
  const { toast } = useToast();
  const router = useRouter();
  return useMutation({
    mutationFn: async (data: {
      masterclassId: number;
      description: string;
      rate: number;
      images: string[];
      authorName: string;
      promoEmail: string;
    }) => {
      return await axios.post(getApiUrl(`/review/create`), data, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    },
    onError: (error) => {
      console.error(error);
      const ax = error as AxiosError<{ error?: { message?: string } }>;
      const msg =
        ax.response?.data?.error?.message ??
        (typeof ax.response?.data === 'string' ? ax.response.data : null);
      toast({
        title: msg || 'Не удалось отправить отзыв',
        variant: 'error',
      });
    },
    onSuccess: () => {
      if (options?.afterSuccess === 'back') {
        toast({
          title: 'Отзыв отправлен на модерацию',
          description:
            'После одобрения на указанный email придёт промокод на 10% скидки.',
          variant: 'success',
        });
        router.back();
        return;
      }
      router.push(`/masterclasses/${masterclassWordId}`);
      toast({
        title: 'Отзыв отправлен на модерацию',
        description:
          'После одобрения на указанный email придёт промокод на 10% скидки.',
        variant: 'success',
      });
    },
  });
};

export const useUploadReviewPhotosMutation = () => {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      for (const f of files) {
        formData.append('files', f);
      }
      return axios.post(getApiUrl('/review/upload-photos'), formData, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    },
  });
};

export const useGetMyReviews = () => {
  const { token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.myReviews],
    queryFn: async () => {
      return await axios.get(getApiUrl(`/review/my`), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    },
    select: (data): IReview[] => data.data || [],
    enabled: !!token,
  });
};

export const useGetMeReviewsCountQuery = () => {
  const { token } = useSession();
  return useQuery({
    queryKey: [QUERY_KEYS.myReviewsCount],
    queryFn: async () => {
      return await axios.get(getApiUrl(`/review/my/count`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    select: (data): number => data?.data || 'Нет отзывов',
    enabled: !!token,
  });
};
